/**
 * Channel value (0-255) → its linear-light component, undoing the sRGB gamma
 * curve. This is the step a naive average misses: sRGB is perceptually encoded,
 * so raw channel values overstate how bright a colour actually is.
 *
 * @param {number} channel
 * @returns {number} 0-1
 */
const toLinear = (channel) => {
  const c = channel / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/**
 * WCAG 2.x relative luminance.
 *
 * @param {string} hex - e.g. '#DC1D1D' (with or without '#')
 * @returns {number} 0 (black) to 1 (white)
 */
export const relativeLuminance = (hex) => {
  const value = hex.replace('#', '')
  const r = parseInt(value.substring(0, 2), 16)
  const g = parseInt(value.substring(2, 4), 16)
  const b = parseInt(value.substring(4, 6), 16)
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

/**
 * WCAG contrast ratio between a colour and pure white / pure black.
 * Ranges 1 (identical) to 21 (black on white).
 *
 * @param {number} luminance - relative luminance, 0-1
 * @returns {{ white: number, black: number }}
 */
const contrastRatios = (luminance) => ({
  white: 1.05 / (luminance + 0.05),
  black: (luminance + 0.05) / 0.05,
})

/**
 * WCAG contrast ratio between two colours: 1 (identical) to 21 (black on white).
 *
 * @param {string} hexA - e.g. '#F2B400'
 * @param {string} hexB
 * @returns {number}
 */
export const contrastRatio = (hexA, hexB) => {
  const [lighter, darker] = [
    relativeLuminance(hexA),
    relativeLuminance(hexB),
  ].sort((a, b) => b - a)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Returns black or white hex color for best text contrast over a background.
 *
 * Uses the WCAG 2.x model — sRGB gamma-corrected relative luminance, then an
 * actual contrast-ratio comparison — rather than a raw weighted average of
 * channel values.
 *
 * The difference is not academic. The old ITU-R BT.601 average judged colour by
 * unlinearised channel values, which understates how bright saturated cyans,
 * greens and yellows really are, so it kept choosing white where white is
 * unreadable:
 *
 *   #06B6D4 (cyan)   white 2.43:1  →  black 8.65:1
 *   #EAB308 (gold)   white 1.92:1  →  black 10.95:1
 *   #F2B400 (amber)  white 1.86:1  →  black 11.30:1
 *
 * WCAG AA wants 4.5:1 for body text, so those were failing outright. Picking
 * whichever of black/white scores higher is what colour libraries do, and it
 * degrades sensibly for mid-tones instead of snapping at an arbitrary cutoff.
 *
 * @param {string} hexColor - Background color hex string (e.g. '#DC1D1D')
 * @returns {string} '#000000' or '#FFFFFF', whichever reads better
 */
export const getContrastTextColor = (hexColor) => {
  if (!hexColor) {
    return '#FFFFFF'
  }

  const luminance = relativeLuminance(hexColor)
  if (Number.isNaN(luminance)) {
    return '#FFFFFF'
  }

  const { white, black } = contrastRatios(luminance)
  return black > white ? '#000000' : '#FFFFFF'
}
